import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { BestsellerProvider } from "./context/BestsellerContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { WishlistProvider } from "./context/WishlistContext";
import { Header, Footer } from "./components/Layout";
import { CookieConsent } from "./components/CookieConsent";
import { HelmetProvider } from 'react-helmet-async';
import { lazy, Suspense, useEffect } from "react";
import { useParams } from "react-router-dom";
import { PromoPopup } from "./components/PromoPopup";
import { ErrorBoundary } from "./components/ErrorBoundary";

import { trackPageView } from "./lib/analytics";

/** Retry wrapper for lazy imports — retries once on chunk load failure */
const lazyRetry = (fn) =>
  lazy(() =>
    fn().catch(() =>
      new Promise((resolve) => setTimeout(resolve, 1500)).then(fn)
    )
  );

const NotFoundPage = lazyRetry(() => import("./pages/NotFoundPage"));
const captureReferral = () => {
  const params = new URLSearchParams(window.location.search);
  const ref = params.get('ref');
  if (ref) {
    localStorage.setItem('ss_referral_code', ref.toUpperCase().trim());
    // Clean URL without reload
    const url = new URL(window.location);
    url.searchParams.delete('ref');
    window.history.replaceState({}, '', url.pathname + url.search);
  }
};
captureReferral();

// Eager-load critical above-the-fold pages
import { HomePage } from "./pages/HomePage";
import { ShopPage } from "./pages/ShopPage";
import BrandPage from "./pages/BrandPage";

// Lazy-load heavier pages
const ProductDetailPage = lazyRetry(() => import("./pages/ProductDetailPage").then(m => ({ default: m.ProductDetailPage })));
const CartPage = lazyRetry(() => import("./pages/CartPage").then(m => ({ default: m.CartPage })));
const CheckoutPage = lazyRetry(() => import("./pages/CheckoutPage").then(m => ({ default: m.CheckoutPage })));
const QuickCheckoutPage = lazyRetry(() => import("./pages/QuickCheckoutPage").then(m => ({ default: m.QuickCheckoutPage })));
const LoginPage = lazyRetry(() => import("./pages/AuthPages").then(m => ({ default: m.LoginPage })));
const RegisterPage = lazyRetry(() => import("./pages/AuthPages").then(m => ({ default: m.RegisterPage })));
const ForgotPasswordPage = lazyRetry(() => import("./pages/AuthPages").then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazyRetry(() => import("./pages/AuthPages").then(m => ({ default: m.ResetPasswordPage })));
const OrdersPage = lazyRetry(() => import("./pages/OrdersPage").then(m => ({ default: m.OrdersPage })));
const AdminDashboard = lazyRetry(() => import("./pages/AdminDashboard").then(m => ({ default: m.AdminDashboard })));
const WishlistPage = lazyRetry(() => import("./pages/WishlistPage").then(m => ({ default: m.WishlistPage })));
const SharedWishlistPage = lazyRetry(() => import("./pages/SharedWishlistPage").then(m => ({ default: m.SharedWishlistPage })));
const SizeGuidePage = lazyRetry(() => import("./components/SizeGuide").then(m => ({ default: m.SizeGuidePage })));
const EventsPage = lazyRetry(() => import("./pages/EventsPage"));
const EventLandingPage = lazyRetry(() => import("./pages/EventLandingPage").then(m => ({ default: m.EventLandingPage })));
const GiftCardsPage = lazyRetry(() => import("./pages/GiftCardsPage").then(m => ({ default: m.GiftCardsPage })));
const GiftCardRedeemPage = lazyRetry(() => import("./pages/GiftCardRedeemPage").then(m => ({ default: m.GiftCardRedeemPage })));
const PaymentSuccessPage = lazyRetry(() => import("./pages/PaymentPages").then(m => ({ default: m.PaymentSuccessPage })));
const PaymentCancelPage = lazyRetry(() => import("./pages/PaymentPages").then(m => ({ default: m.PaymentCancelPage })));
const PublicTrackingPage = lazyRetry(() => import("./pages/PublicTrackingPage").then(m => ({ default: m.PublicTrackingPage })));
const LoyaltyPage = lazyRetry(() => import("./pages/LoyaltyPage").then(m => ({ default: m.LoyaltyPage })));
const ReferralPage = lazyRetry(() => import("./pages/ReferralPage").then(m => ({ default: m.ReferralPage })));
const AddressLabelsPage = lazyRetry(() => import("./pages/admin/AddressLabelsPage"));
const PackingSlipsPage = lazyRetry(() => import("./pages/admin/PackingSlipsPage"));
const PopUpSalePage = lazyRetry(() => import("./pages/admin/PopUpSalePage"));
const QrLabelsPage = lazyRetry(() => import("./pages/admin/QrLabelsPage"));
const GiftCardSuccessPage = lazyRetry(() => import("./pages/GiftCardSuccessPage"));
const ChristmasGiftEditPage = lazyRetry(() => import("./pages/ChristmasGiftEditPage"));
const CollectionsListPage = lazyRetry(() => import("./pages/CollectionsListPage").then(m => ({ default: m.CollectionsListPage })));
const CollectionPage = lazyRetry(() => import("./pages/CollectionPage").then(m => ({ default: m.CollectionPage })));
const DepartmentCollectionPage = lazyRetry(() => import("./pages/DepartmentCollectionPage").then(m => ({ default: m.DepartmentCollectionPage })));
const AccountSettingsPage = lazyRetry(() => import("./pages/AccountSettingsPage"));
const ReturnsPage = lazyRetry(() => import("./pages/ReturnsPage"));
const ReturnLabelPage = lazyRetry(() => import("./pages/ReturnLabelPage"));
const OrderLookupPage = lazyRetry(() => import("./pages/OrderLookupPage"));
const DeliveryPage = lazyRetry(() => import("./pages/DeliveryPage"));
const FAQsPage = lazyRetry(() => import("./pages/FAQsPage"));
const OurStoryPage = lazyRetry(() => import("./pages/OurStoryPage"));
const WelcomePopup = lazyRetry(() => import("./components/WelcomePopup").then(m => ({ default: m.WelcomePopup })));

const PageLoader = () => (
  <div className="min-h-screen bg-[#F7F7EB] flex items-center justify-center">
    <div className="flex flex-col items-center gap-3">
      <div className="w-8 h-8 border-2 border-[#000000] border-t-transparent rounded-full animate-spin" />
      <span className="text-sm text-[#737373] tracking-wide">Loading...</span>
    </div>
  </div>
);

const DEPT_SLUGS = new Set(['dance', 'activewear', 'accessories']);
const CollectionRouter = () => {
  const { slug } = useParams();
  return DEPT_SLUGS.has(slug) ? <DepartmentCollectionPage /> : <CollectionPage />;
};


// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { isAdmin, isAuthenticated, loading } = useAuth();
  const location = useLocation();
  if (loading) return <PageLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
};

const MainLayout = ({ children }) => (
  <>
    <Header />
    <main id="main-content">{children}</main>
    <Footer />
  </>
);

const AuthLayout = ({ children }) => (
  <main>{children}</main>
);

function AppRoutes() {
  return (
    <ErrorBoundary>
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<MainLayout><HomePage /></MainLayout>} />
        <Route path="/shop" element={<MainLayout><ShopPage /></MainLayout>} />
        <Route path="/brand/:brandSlug" element={<MainLayout><BrandPage /></MainLayout>} />
        <Route path="/product/:productId" element={<MainLayout><ProductDetailPage /></MainLayout>} />
        <Route path="/cart" element={<MainLayout><CartPage /></MainLayout>} />
        
        <Route path="/login" element={<AuthLayout><LoginPage /></AuthLayout>} />
        <Route path="/register" element={<AuthLayout><RegisterPage /></AuthLayout>} />
        <Route path="/forgot-password" element={<AuthLayout><ForgotPasswordPage /></AuthLayout>} />
        <Route path="/reset-password" element={<AuthLayout><ResetPasswordPage /></AuthLayout>} />
        
        <Route path="/size-guide" element={<MainLayout><SizeGuidePage /></MainLayout>} />
        <Route path="/checkout" element={
          <MainLayout><CheckoutPage /></MainLayout>
        } />
        <Route path="/quick-checkout" element={
          <ProtectedRoute><AuthLayout><QuickCheckoutPage /></AuthLayout></ProtectedRoute>
        } />
        <Route path="/account/orders" element={
          <ProtectedRoute><MainLayout><OrdersPage /></MainLayout></ProtectedRoute>
        } />
        <Route path="/account/settings" element={
          <ProtectedRoute><MainLayout><AccountSettingsPage /></MainLayout></ProtectedRoute>
        } />
        <Route path="/account/returns" element={
          <ProtectedRoute><MainLayout><ReturnsPage /></MainLayout></ProtectedRoute>
        } />
        <Route path="/account/returns/:returnId/label" element={
          <ProtectedRoute><ReturnLabelPage /></ProtectedRoute>
        } />
        <Route path="/wishlist" element={
          <ProtectedRoute><MainLayout><WishlistPage /></MainLayout></ProtectedRoute>
        } />
        
        <Route path="/admin/address-labels" element={<AdminRoute><AddressLabelsPage /></AdminRoute>} />
        <Route path="/admin/packing-slips" element={<AdminRoute><PackingSlipsPage /></AdminRoute>} />
        <Route path="/admin/pop-up" element={<AdminRoute><PopUpSalePage /></AdminRoute>} />
        <Route path="/admin/qr-labels" element={<AdminRoute><QrLabelsPage /></AdminRoute>} />
        <Route path="/admin" element={
          <AdminRoute><MainLayout><AdminDashboard /></MainLayout></AdminRoute>
        } />
        
        <Route path="/events" element={<MainLayout><EventsPage /></MainLayout>} />
        <Route path="/events/:slug" element={<MainLayout><EventLandingPage /></MainLayout>} />
        <Route path="/gift-cards" element={<MainLayout><GiftCardsPage /></MainLayout>} />
        <Route path="/gift-cards/success" element={<MainLayout><GiftCardSuccessPage /></MainLayout>} />
        <Route path="/redeem/:code" element={<MainLayout><GiftCardRedeemPage /></MainLayout>} />
        <Route path="/christmas-gift-edit" element={<MainLayout><ChristmasGiftEditPage /></MainLayout>} />
        <Route path="/collections" element={<MainLayout><CollectionsListPage /></MainLayout>} />
        <Route path="/collections/:slug" element={<MainLayout><CollectionRouter /></MainLayout>} />
        <Route path="/shared-wishlist/:shareId" element={<MainLayout><SharedWishlistPage /></MainLayout>} />
        <Route path="/track" element={<MainLayout><PublicTrackingPage /></MainLayout>} />
        <Route path="/order-lookup" element={<MainLayout><OrderLookupPage /></MainLayout>} />
        <Route path="/loyalty" element={<MainLayout><LoyaltyPage /></MainLayout>} />
        <Route path="/delivery" element={<MainLayout><DeliveryPage /></MainLayout>} />
        <Route path="/faqs" element={<MainLayout><FAQsPage /></MainLayout>} />
        <Route path="/our-story" element={<MainLayout><OurStoryPage /></MainLayout>} />
        <Route path="/referrals" element={<ProtectedRoute><MainLayout><ReferralPage /></MainLayout></ProtectedRoute>} />
        <Route path="/payment/success" element={<MainLayout><PaymentSuccessPage /></MainLayout>} />
        <Route path="/payment/cancel" element={<MainLayout><PaymentCancelPage /></MainLayout>} />
        
        <Route path="*" element={<MainLayout><NotFoundPage /></MainLayout>} />
      </Routes>
    </Suspense>
    </ErrorBoundary>
  );
}

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); trackPageView(pathname); }, [pathname]);
  return null;
};

function App() {
  return (
    <HelmetProvider>
    <div className="App">
      <a href="#main-content" className="skip-to-content">Skip to content</a>
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <WishlistProvider>
              <BestsellerProvider>
              <ScrollToTop />
              <AppRoutes />
              <CookieConsent />
              <Suspense fallback={null}><WelcomePopup /></Suspense>
              <PromoPopup />
              <Toaster position="top-right" richColors />
              </BestsellerProvider>
            </WishlistProvider>
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
    </HelmetProvider>
  );
}

export default App;
